import ListView, { ListViewItem } from '@ingka/list-view';
import Modal, { ModalBody, ModalHeader, Sheets } from '@ingka/modal';
import {
    selectAssetCreationDate,
    selectAssetDetails,
    selectAssetStatus,
    selectCreatedBy,
    selectCustomerMeetingPoint,
    selectOrderPaymentStatus,
    selectOrderType,
} from '@repo/api-adapters/selectors/customer-assets.selectors';
import { translate } from '@repo/phrase';
import { formatDateTime } from '@repo/utils';

import { QueryError } from '@/components/query-error';

import { useAsset, useAssetParams } from '@/hooks/asset.hooks';
import { getAppConfig } from '@/utils/window.utils';

import { ACTION_BUTTONS_CONFIG, ASSET_ACTIONS_CONFIG } from '../../status-header.config';
import { getComponentConfig } from '../asset-context-buttons.utils';

import styles from './styles.module.scss';

interface Props {
    visible: boolean;
    onClose: () => void;
}

export const AssetContextDropdownModal = ({ visible, onClose }: Props) => {
    const { assetId, assetType } = useAssetParams();
    const { data, isError, isLoading, error } = useAsset(assetType, assetId);
    const assetDetails = selectAssetDetails(data);

    if (isError && !data) {
        return <QueryError error={error} />;
    }
    // TODO: decide what happens when isLoading or order is undefined
    if (isLoading || !assetDetails) {
        return;
    }

    const assetStatus = selectAssetStatus(data);
    const assetDate = selectAssetCreationDate(data);
    const customerMeetingPoint = selectCustomerMeetingPoint(data);
    const createdBy = selectCreatedBy(data);
    const orderPaymentStatus = selectOrderPaymentStatus(data);
    const orderType = selectOrderType(data);

    const buttonsConfig = getComponentConfig(ASSET_ACTIONS_CONFIG, assetType, orderType);

    if (!buttonsConfig) {
        return null;
    }

    return (
        <Modal handleCloseBtn={onClose} visible={visible} data-testid="dropdown-menu-modal">
            <Sheets aria-label={translate('asset_context_menu')} header={<ModalHeader />} footer={null}>
                <ModalBody>
                    <ListView id="order-header-dropdown-modal-data" showDivider className={String(styles.data)}>
                        <ListViewItem
                            emphasised
                            title={translate('order_status_header_status')}
                            description={translate(`asset_list_table_status_${assetStatus}`)}
                        />
                        {orderPaymentStatus && (
                            <ListViewItem
                                emphasised
                                title={translate('order_status_header_payment_status')}
                                description={translate(`payment_status_${orderPaymentStatus}`)}
                            />
                        )}
                        <ListViewItem
                            emphasised
                            title={translate('order_status_header_created')}
                            description={assetDate ? formatDateTime(assetDate, getAppConfig().dateTimeFormat) : '-'}
                        />
                        <ListViewItem
                            emphasised
                            title={translate('order_status_header_cmp')}
                            description={customerMeetingPoint ?? '-'}
                        />
                        <ListViewItem
                            emphasised
                            title={translate('order_status_header_sales_person')}
                            description={createdBy}
                        />
                    </ListView>
                    <ListView id="order-header-dropdown-modal-action" showDivider>
                        {buttonsConfig.map((key) => {
                            const ActionComponent = ACTION_BUTTONS_CONFIG[key];
                            if (!ActionComponent) {
                                return null;
                            }

                            return <ActionComponent key={key} onClose={onClose} context="modal" type="listitem" />;
                        })}
                    </ListView>
                </ModalBody>
            </Sheets>
        </Modal>
    );
};
